import React, { useState, useEffect } from 'react';
import {
    Typography,
    List,
    ListItem,
    ListItemText,
    Paper,
    CircularProgress,
    CardContent,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import PushNotificationSolidIcon from '@brightlayer-ui/icons-mui/PushNotificationSolid';
import LockIcon from '@mui/icons-material/Lock';
import { useSelector, useDispatch } from 'react-redux';
import { ChangePasswordDialog } from '../../components/ChangePasswordDialog/ChangePasswordDialog';
import { GenericToolbar } from '../../components/GenericToolbar';
import { ContentContainer } from '../../components/ContentContainer';
import { getUserId, useAuthState, useAuthHelper } from '../../components/AuthContextProvider';
import { Actions,Thunks } from '../../actions';
import { Selectors } from '../../selectors';
import { ConfirmationPopUp } from '../../components/ConfirmationPopUp/ConfirmationPopUp';
import * as PXBColors from '@brightlayer-ui/colors';
import { UserDetails } from '../../components/UserDetails/UserDetails';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { UserLocation } from '../../components/UserLocations/UserLocations';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { Data, isDataLoaded, OrganizationData, SiteRoleData } from '../../types';
import { useAppOrganizationData } from '../../components/AppDataProvider';
import { useUserRoleOperationData } from '../../data/DataProviders/RoleOperationDataProvider';
import { bindRoleLocationReadOnlyView } from '../../lib/user-helpers';
import ContentLoader from 'react-content-loader';
import { useLocationListData } from '../../data/DataProviders/LocationListDataProvider';
import { NavigationPromptModal } from '../../components/NavigationPromptModal/NavigationPromptModal';


const useStyles = makeStyles(() => ({
    cardContent: {
        flex: '1 1 0px',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    largeIcon: {
        width: 60,
        height: 60,
        color: PXBColors.gray[500],
    },
}));

export const Settings: React.FC = (props): JSX.Element => {
    const { t } = useLanguageLocale();
    const classes = useStyles(props);
    const authState = useAuthState();
    const roleData = useUserRoleOperationData();
    const userData = useSelector(Selectors.User.user);   
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const selectedOrganization = useSelectedOrganizationData();
    const locationsList = useLocationListData();
    const locationListData = locationsList[0];
    const [emailStatus, setEmailStatus] = useState(false);
    const [smsStatus, setSmsStatus] = useState(false);
    const [automaticLogOut, setAutomaticLogOut] = useState(true);
    const [edit, setEdit] = useState(false);

    const [showDialog, setShowDialog] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [roleLocationList, setRoleLocationList] = useState<SiteRoleData[]>([]);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const orgId = isDataLoaded(locationListData) ? locationListData.value.organizationId : '';
    const locationList: Data<OrganizationData> = useAppOrganizationData(orgId);
    const user = userData.loggedInUser;
    const userId = getUserId(authState) || '';
    const { addToast } = useToasts();

    const [organization, setOrganization] = useState<{ id: string; name: string }>({ id: '', name: '' });

    useEffect(() => {
        if (
            isDataLoaded(roleData) &&
            isDataLoaded(locationList) &&
            isRecordFound(selectedOrganization) &&
            isDataLoaded(locationListData)
        ) {
            setOrganization(selectedOrganization.value);
            const result = bindRoleLocationReadOnlyView(roleData.value, locationListData.value);
            setRoleLocationList(result);
        }
    }, [roleData, locationList]);

    const hasDirty = (value: boolean): void => {
        setIsDirty(value);
        setConfirmDiscard(false);
    };
    
    useEffect(() => {
        if (user && user.notification) {
            setEmailStatus(user.notification.emailenabled);
            setSmsStatus(user.notification.smsenabled || false);
        }
        setAutomaticLogOut(user && user.session.sessionEnabled);

        if (!userData.userDetailLoading && userData.updateSuccess) {
            addToast(t('USERDETAILS.EDIT_SUCCESS'));
            setEdit(false);
            dispatch(Actions.UserSettings.reset());
            hasDirty(false);
        }
       
    }, [user]);// eslint-disable-line react-hooks/exhaustive-deps

    const updateEdit = (value: boolean): void => {
        setEdit(value);
    }


    const closePopUp = (): void => {
        setShowDialog(false);
        setShowConfirmation(false);
        setShowDisclaimer(false);
    };
    const openPasswordDialog = (): void => setShowDialog(true);
    const openSmsDialog = (value: boolean): void => setShowConfirmation(value);
    const openDisclaimer = (): void => setShowDisclaimer(true);

    
    const handleEmail = (value: boolean): void => {
        if (userId)
            dispatch(
                Thunks.UserSettings.updateUserData(
                    value,
                    user.notification.smsenabled,
                    user.session.sessionEnabled,
                    userId,
                    authHelper,
                    true
                )
            );
    };

    const handleSms = (value: boolean): void => {
        if (userId)
            dispatch(
                Thunks.UserSettings.updateUserData(
                    user.notification.emailenabled,
                    value,
                    user.session.sessionEnabled,
                    userId,
                    authHelper,
                    false
                )
            );
            openSmsDialog(false);
        // setTextNotificationStatus(value);
    };

    const handleSmsConfirmation = (value: boolean): void => {
        if (userId)
            dispatch(
                Thunks.UserSettings.updateUserData(
                    user.notification.emailenabled,
                    value,
                    user.session.sessionEnabled,
                    userId,
                    authHelper,
                    false
                )
            );
        setShowConfirmation(false);
        // setTextNotificationStatus(value);
    };
    const disableAutomaticLogout = (value: boolean): void => {
        if (userId)
            dispatch(
                Thunks.UserSettings.updateUserSessionData(
                    user.notification.emailenabled,
                    user.notification.smsenabled,
                    value,
                    userId,
                    authHelper
                )
            );
        setShowDisclaimer(false);
    };
    const onSumbit = (firstName: string, lastName: string, phone: string, countryCode: string): void => {
        if (userId)
            dispatch(
                Thunks.UserSettings.updateUserDetails(
                    userId,
                    firstName,
                    lastName,
                    phone,
                    countryCode,
                    user.user.email,
                    authHelper,
                    addToast
                )
            );
    };
   
    return (
        <>
            <GenericToolbar title={t('SETTINGS.TITLE')} />

            <ContentContainer>
                <div data-testid="success-body">
                    {userData.loading && (
                        <CardContent className={classes.cardContent}>
                            <CircularProgress id="progress-spinner" />
                        </CardContent>
                    )}

                    {!userData.loading && (
                        <>
                            <Paper>
                                <List
                                    component="div"
                                    disablePadding
                                    subheader={
                                        <ListItem key="aad" divider={true} style={{ backgroundColor: 'transparent' }}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h6" color="primary">
                                                        {t('USER_SETTINGS.ORGANIZATION')}
                                                    </Typography>
                                                }
                                            ></ListItemText>
                                        </ListItem>
                                    }
                                >
                                    {!isDataLoaded(roleData) && !isDataLoaded(locationList) && (
                                        <ContentLoader
                                            speed={2}
                                            width={500}
                                            height={180}
                                            viewBox="0 0 500 180"
                                            color="#f5f5f5"
                                        >
                                            <rect x="70" y="52" rx="6" ry="6" width="483" height="50" />
                                            <circle cx="35" cy="80" r="15" />
                                        </ContentLoader>
                                    )}
                                    {isDataLoaded(roleData) && isDataLoaded(locationList) && (
                                        <UserLocation
                                            location={roleLocationList}
                                            organizationName={organization.name}
                                        />
                                    )}
                                </List>
                            </Paper>

                            <UserDetails
                                userId={userId}
                                userData={userData.loggedInUser}
                                userState={userData}
                                userUpdateLoading={userData.userDetailLoading}
                                onSubmit={onSumbit}
                                openPassword={openPasswordDialog}
                                openSMSModal={openSmsDialog}
                                openDisclaimer={openDisclaimer}
                                disableAutomaticLogout={disableAutomaticLogout}
                                hasDirty={hasDirty}
                                confirmDiscard={confirmDiscard}
                                loggedinUser={true}
                                loggedUser={true}
                                handleEmail={handleEmail}
                                handleSms={handleSms}
                                emailStatus={emailStatus}
                                smsStatus={smsStatus}
                                automaticLogOut={automaticLogOut}
                                edit={edit}
                                updateEdit={updateEdit}
                            ></UserDetails>
                        </>
                    )}
                </div>
            </ContentContainer>
            {showDialog && <ChangePasswordDialog show={showDialog} closeDialog={closePopUp}></ChangePasswordDialog>}
            {showConfirmation && (
                <ConfirmationPopUp
                    icon={<PushNotificationSolidIcon className={classes.largeIcon} />}
                    title={t('USER_SETTINGS.TEXT_NOTIFICATION_TITLE')}
                    body={<Typography> {t('USER_SETTINGS.TEXT_NOTIFICATION_BODY')}</Typography>}
                    okButtonText={t('ACTIONS.ENABLE')}
                    cancelButtonText={t('ACTIONS.CANCEL')}
                    show={showConfirmation}
                    closeDialog={closePopUp}
                    handleOk={handleSmsConfirmation}
                    width="xs"
                    okButtonShow={true}
                    cancelButtonsShow={true}
                ></ConfirmationPopUp>
            )}
            {showDisclaimer && (
                <ConfirmationPopUp
                    icon={<LockIcon className={classes.largeIcon} />}
                    title={t('USER_SETTINGS.AUTOMATIC_LOGOUT_TITLE')}
                    body={
                        <>
                            <Typography variant="subtitle2" style={{ marginBottom: 30 }}>
                                {t('USER_SETTINGS.AUTOMATIC_LOGOUT_SUBTITLE')}
                            </Typography>

                            <Typography variant="subtitle2" component="p" style={{ marginBottom: 40 }}>
                                <b>{t('USER_SETTINGS.WARNING')}</b>: {t('USER_SETTINGS.AUTOMATIC_LOGOUT_BODY')}
                            </Typography>
                        </>
                    }
                    okButtonText={t('ACTIONS.DISABLE')}
                    cancelButtonText={t('ACTIONS.CANCEL')}
                    show={showDisclaimer}
                    closeDialog={closePopUp}
                    handleOk={(): void => {
                        disableAutomaticLogout(false);
                    }}
                    width="xs"
                    okButtonShow={true}
                    cancelButtonsShow={true}
                ></ConfirmationPopUp>
            )}
            <NavigationPromptModal isDirty={isDirty} />
        </>
    );
};
export default Settings;
